// Implements: F-UIX-018, F-OBS-011
/**
 * The audit list's pager (UIX.7): one read per asked action, merged newest first.
 *
 * The server filters by one action token at a time, so "every action" and a single action are one
 * stream, and "all revocations" (a person's and the system's) is two. Each stream follows the
 * server's own keyset cursor. A page ends when a stream that still has rows on the server runs out
 * of read ones, so a row is never shown before an older row of the other stream that has not been
 * read yet. With one stream a page is exactly the server's page.
 *
 * The reading itself is injected (`FetchStream`), so the merge is tested without a network.
 */

import type { ActionFilter, AuditAction, AuditPageView, AuditRowView } from "../../../types/audit";

/** The two revocation tokens "all revocations" covers, in the server's order. */
export const REVOCATIONS: readonly AuditAction[] = ["share_link_revoked", "share_link_revoked_by_system"];

/** One read per entry: `undefined` asks every action. */
export function actionsOf(filter: ActionFilter): readonly (AuditAction | undefined)[] {
  if (filter === "") return [undefined];
  if (filter === "all_revocations") return REVOCATIONS;
  return [filter];
}

/** One action's read: the server's cursor and the rows read but not shown yet. */
export interface AuditStream {
  /** The cursor of the next server page; `null` before the first read and after the last one. */
  readonly cursor: string | null;
  readonly started: boolean;
  readonly buffer: readonly AuditRowView[];
}

export interface AuditPagerState {
  readonly streams: readonly AuditStream[];
}

export function firstPagerState(streams: number): AuditPagerState {
  return { streams: Array.from({ length: streams }, () => ({ cursor: null, started: false, buffer: [] })) };
}

/** Whether the server still has rows of this stream that were not read. */
function hasMore(stream: AuditStream): boolean {
  return !stream.started || stream.cursor !== null;
}

export interface MergedAuditPage {
  /** The days the server covered, as this page's first answer echoed them; `null` if it read nothing. */
  readonly range: { readonly from: string; readonly to: string } | null;
  readonly items: readonly AuditRowView[];
  /** Where the next page starts, or `null` when nothing is left. */
  readonly next: AuditPagerState | null;
}

/** Reads one server page of stream `index` from `cursor` (`null`: the first page). */
export type FetchStream = (index: number, cursor: string | null) => Promise<AuditPageView>;

/** The index of the stream whose next row is the newest, or `-1` when every buffer is empty. */
function newestHead(buffers: readonly (readonly AuditRowView[])[]): number {
  let newest = -1;
  let newestAt = Number.NEGATIVE_INFINITY;
  buffers.forEach((buffer, index) => {
    const head = buffer[0];
    if (head === undefined) return;
    const at = Date.parse(head.occurred_at);
    if (newest === -1 || at > newestAt) {
      newest = index;
      newestAt = at;
    }
  });
  return newest;
}

export async function nextAuditPage(state: AuditPagerState, fetchStream: FetchStream): Promise<MergedAuditPage> {
  const answers = await Promise.all(
    state.streams.map((stream, index) =>
      stream.buffer.length > 0 || !hasMore(stream) ? Promise.resolve(null) : fetchStream(index, stream.cursor),
    ),
  );
  const streams = state.streams.map((stream, index): AuditStream => {
    const answer = answers[index];
    return answer === null || answer === undefined
      ? stream
      : { cursor: answer.next_cursor, started: true, buffer: [...stream.buffer, ...answer.items] };
  });
  const first = answers.find((answer) => answer !== null) ?? null;

  const buffers = streams.map((stream) => [...stream.buffer]);
  const items: AuditRowView[] = [];
  // A stream with rows still on the server but none read cannot be ordered against: stop there.
  while (!streams.some((stream, index) => hasMore(stream) && buffers[index]?.length === 0)) {
    const taken = buffers[newestHead(buffers)]?.shift();
    if (taken === undefined) break;
    items.push(taken);
  }

  const rest = streams.map((stream, index) => ({ ...stream, buffer: buffers[index] ?? [] }));
  const done = rest.every((stream) => stream.buffer.length === 0 && !hasMore(stream));
  return {
    range: first === null ? null : { from: first.from, to: first.to },
    items,
    next: done ? null : { streams: rest },
  };
}
